<template>
    <div>
        <div :class="['form-group', { 'is-focused': isFocused }]">
            <input
                :id="id"
                class="form-control"
                type="file"
                :accept="accept"
                :placeholder="placeholder"
                :value="value"
                @focus="onFocus"
                @blur="onBlur"
                @input="onInput"
                @change="onChange"
            />
            <label class="control-label" :for="id">
                <span v-if="fileUploading" class="file-uploading">
                    <l-spinner /> Uploading ...
                </span>
                <span v-else>
                    <i class="fa fa-upload"></i>
                    {{ fileName || placeholder }}
                </span>
            </label>
        </div>
        <!-- Error Size -->
        <div v-if="errorSize">
            <slot name="error">
                <l-text-alert
                    type="danger"
                    :text="$t('learning.admin.element.details.error.file.size', { size })"
                />
            </slot>
        </div>
        <!--/Error Size -->
    </div>
</template>

<script>
import generateDefaultId from "@/utils/generate-default-id";

import LTextAlert from "@/components/LTextAlert";
import LSpinner from "@/components/LSpinner";

export default {
    components: {
        LTextAlert,
        LSpinner,
    },
    props: {
        placeholder: {
            type: String,
            default: "",
        },
        accept: {
            type: String,
            required: true,
        },
        value: {
            type: String,
            required: true,
        },
        fileUploading: {
            type: Boolean,
            default: false,
        },
        size: {
            type: Number,
            default: 5120, // MB
        },
        id: {
            type: String,
            default: generateDefaultId(),
        },
    },
    data() {
        return {
            isFocused: false,
            errorSize: false,
        };
    },
    computed: {
        fileName() {
            if (!this.value) {
                return;
            }
            return String(this.value)
                .split("\\")
                .pop();
        },
    },
    methods: {
        onFocus() {
            this.isFocused = true;
            this.errorSize = false;
        },
        onBlur() {
            this.isFocused = false;
        },
        onInput(e) {
            this.$emit("input", e.target.value);
        },
        onChange(e) {
            const { files } = e.target;

            // Validate files presence
            if (!files) {
                return;
            }

            // Validate files size
            const filesSize =
                Array.from(files).reduce((total, file) => total + file.size, 0) / (1024 * 1024); // in MB
            if (filesSize > this.size) {
                this.errorSize = true;
                this.value = null;
                return;
            }

            this.$emit("change", files);
        },
    },
};
</script>

<style scoped>
input[type="file"].form-control {
    width: 1px;
    height: 1px;
    opacity: 0;
    overflow: hidden;
    position: absolute;
    z-index: -1;
}

input[type="file"].form-control + label {
    font-size: 2rem;
    font-weight: bold;
    padding: 4rem;
    display: block;
    text-align: center;
    margin: 0;
    border: 2px dashed var(--color-grey-light);
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 200ms ease-in;
}
input[type="file"].form-control:focus + label,
input[type="file"].form-control + label:hover {
    background-color: #f9f9f9;
}
input[type="file"].form-control:focus + label {
    outline: 1px dotted var(--color-grey);
    outline: -webkit-focus-ring-color auto 5px;
}

label i {
    margin-right: 4px;
}

.file-uploading {
    display: flex;
    justify-content: center;
}
.file-uploading >>> .l-spinner {
    margin-right: 8px;
}
</style>
